import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { explainCopy } from '../../lib/explainCopy'
import type { SimRunResponse } from '../../types/electionSim'

const nf = new Intl.NumberFormat('en-US')
const signedInt = (n: number) => `${n >= 0 ? '+' : '−'}${nf.format(Math.abs(Math.round(n)))}`
// Efficiency cells render the bare signed decimal (−1.7 / 14.5) to match the design mockup.
const effCell = (n: number) => (n >= 0 ? n.toFixed(1) : `−${Math.abs(n).toFixed(1)}`)

/** Dashed-underline hover anchor for a row-label tooltip (mirrors LeverPanel's LeverLabel). */
function TipLabel({ text, tip }: { text: string; tip: string }) {
  return (
    <Tooltip content={tip} openDelay={0} showArrow contentProps={{ maxW: "320px" }} positioning={{
      placement: "top"
    }}>
      <Text as="span" tabIndex={0} cursor="help" borderBottomWidth="1px" borderBottomStyle="dashed" borderBottomColor="accent">
        {text}
      </Text>
    </Tooltip>
  );
}

/**
 * Untargeted-vs-targeted compare strip (P3): a compact 3-row delta table shown above the OutcomeCard
 * when a run carries an `untargetedComparison`. The targeted ("Smart target") column reads off the run
 * itself; the "Whole district" column off the comparison block. Efficiency (net/1k) is the honest
 * per-effort comparator. Renders nothing when there is no comparison block.
 */
export function CompareStrip({ run }: { run: SimRunResponse }) {
  const cmp = run.untargetedComparison
  if (!cmp) return null

  const tgtNet = run.netVotesMoved ?? 0
  const untNet = cmp.netVotesMoved
  const deltaNet = tgtNet - untNet
  const tgtEff = run.efficiencyNetPer1k ?? 0

  return (
    <Box bg="surface" p={5} borderRadius="lg" shadow="md" data-testid="compare-strip">
      <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase" color="gray.500" mb={3}>
        Untargeted vs. targeted
      </Text>

      {/* header row */}
      <HStack fontSize="xs" color="gray.500" fontWeight="semibold" mb={1}>
        <Box flex="2" />
        <Box flex="1.4" textAlign="right">Whole district</Box>
        <Box flex="1.4" textAlign="right">Smart target</Box>
        <Box flex="1" textAlign="right">Δ</Box>
      </HStack>

      <VStack align="stretch" gap={2} fontSize="sm">
        {/* Net margin shift */}
        <HStack>
          <Box flex="2"><TipLabel text="Net margin shift" tip={explainCopy.compareToUntargeted()} /></Box>
          <Box flex="1.4" textAlign="right" color={untNet >= 0 ? 'green.600' : 'red.500'} fontWeight="semibold">{signedInt(untNet)}</Box>
          <Box flex="1.4" textAlign="right" color={tgtNet >= 0 ? 'green.600' : 'red.500'} fontWeight="semibold">{signedInt(tgtNet)}</Box>
          <Box flex="1" textAlign="right" fontWeight="bold">{signedInt(deltaNet)}</Box>
        </HStack>

        {/* Reachable / contacted */}
        <HStack color="gray.700">
          <Box flex="2">Reachable / contacted</Box>
          <Box flex="1.4" textAlign="right">{nf.format(cmp.totalReachableVoters)} / {nf.format(cmp.totalContacts)}</Box>
          <Box flex="1.4" textAlign="right">{nf.format(run.totalReachableVoters)} / {nf.format(run.totalContacts)}</Box>
          <Box flex="1" />
        </HStack>

        {/* Efficiency */}
        <HStack>
          <Box flex="2"><TipLabel text="Efficiency (net/1k)" tip={explainCopy.efficiencyNetPer1k()} /></Box>
          <Box flex="1.4" textAlign="right" color={cmp.efficiencyNetPer1k >= 0 ? 'green.600' : 'red.500'} fontWeight="semibold">{effCell(cmp.efficiencyNetPer1k)}</Box>
          <Box flex="1.4" textAlign="right" color={tgtEff >= 0 ? 'green.600' : 'red.500'} fontWeight="semibold">{effCell(tgtEff)}</Box>
          <Box flex="1" textAlign="right" fontSize="xs" color="gray.500">honest comparator</Box>
        </HStack>
      </VStack>
    </Box>
  );
}
